// eslint-disable-next-line import/no-named-as-default
import { Icon } from "@mdi/react";

import { mdiDomain, mdiHomeLightbulb, mdiImageFilterHdr } from "@mdi/js";
import styled from "styled-components";
import { getFilesFromEvent } from "utils/upload/getFilesFromEvent";
import { uploadDroppedItems } from "utils/upload/uploadDroppedItems";

import { IconSpinner } from "components/icons";
import { useShapefileContext } from "components/project/shapefiles/context";
import { useUploadProjectShapefileList } from "components/project/shapefiles/hooks";

import { SHAPEFILE_TREE_SECTIONS } from "../../../../../../hooks";
import { LayerActionsWrapper } from "../../../../shared";
import { useRightClickContextMenu } from "../../../../shared/hooks/useRightClickContextMenu";
import { FlexItem, NodeTitle } from "../../../../shared/nodes/StyledNodePartials";
import { RootFolderActions } from "../../../../shared/shapefile-folder-actions/RootFolderActions";
import { useStructuredNodeContext } from "../../context/StructuredNodeContext";
import { setCanDrop } from "../../context/reducer/structuredNodeReducer";

export interface IRootShapefileFolder {
  node;
}

export function RootShapefileFolder({ node }: IRootShapefileFolder) {
  const [{ canDrop }, nodeDispatch] = useStructuredNodeContext();
  const { isLoadingColors } = useShapefileContext();
  const uploadShapefileList = useUploadProjectShapefileList();
  const newFolderPosition =
    Math.max(...node.children.map((child) => child.order || 0)) + 1;

  const { openContextMenu, contextMenuObject, nodeRef } = useRightClickContextMenu();

  const isProjectShapefiles = node.key === SHAPEFILE_TREE_SECTIONS.PROJECT;

  return (
    <NodeTitle
      ref={nodeRef}
      onContextMenu={openContextMenu}
      data-testid="root-shapefile-folder-container"
      className="visible-always"
      {...(isProjectShapefiles
        ? {
            canDrop,
            onDragOver: (e: React.DragEvent) => {
              e.preventDefault();
              const files = getFilesFromEvent(e);
              setCanDrop(nodeDispatch, files.length > 0);
            },
            // A dropped folder has to be read before it can be uploaded, and the
            // browser empties the dropped list as soon as this handler returns, so
            // the list is handed over before the first await
            onDrop: async (ev: React.DragEvent) => {
              ev.preventDefault();
              setCanDrop(nodeDispatch, false);
              await uploadDroppedItems({
                transfer: ev.dataTransfer,
                shapefileNodeId: "",
                upload: uploadShapefileList.mutate
              });
            },
            onDragEnter: (e: React.DragEvent) => {
              const files = getFilesFromEvent(e);
              setCanDrop(nodeDispatch, files.length > 0);
            },
            onDragLeave: () => {
              setCanDrop(nodeDispatch, false);
            }
          }
        : {})}>
      <FlexItem>
        {node.key === SHAPEFILE_TREE_SECTIONS.PROJECT && (
          <StyledIcon
            path={mdiHomeLightbulb}
            className="activity-action-icon"
            size={"16px"} //slightly larger to visually match other icons
          />
        )}
        {node.key === SHAPEFILE_TREE_SECTIONS.ORGANIZATION && (
          <StyledIcon path={mdiDomain} className="activity-action-icon" size={1} />
        )}
        {node.key === SHAPEFILE_TREE_SECTIONS.MCDANIEL && (
          <StyledIcon
            path={mdiImageFilterHdr}
            className="activity-action-icon"
            size={"16px"} //slightly larger to visually match other icons
          />
        )}

        <label data-testid="root-shapefile-folder-title">{node.title}</label>
        {isLoadingColors && node.title === SHAPEFILE_TREE_SECTIONS.PROJECT && (
          <StyledSpinner />
        )}
      </FlexItem>

      {node.title === SHAPEFILE_TREE_SECTIONS.PROJECT ? (
        <>
          <LayerActionsWrapper>
            <RootFolderActions
              newFolderPosition={newFolderPosition}
              shapefileNodeId={"root"}
              contextMenu={contextMenuObject}
              folderName={node.title}
            />
          </LayerActionsWrapper>
        </>
      ) : null}
    </NodeTitle>
  );
}

const StyledIcon = styled(Icon)`
  cursor: default;
  color: #a2aaad;
`;

const StyledSpinner = styled(IconSpinner)`
  width: 13px;
  height: 13px;
`;
